import { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, TextInput, View } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';

/**
 * EditDraftModal - Edit a draft's body before sending.
 *
 * Pre-populates with the draft's existing subject + content. On save, the
 * orchestrator inserts a `user_written` row with the edited content,
 * discards every active draft in the negotiation (including the original),
 * and dispatches `send_approved_draft` on the new row.
 *
 * Cancel is non-destructive: the original draft stays editable from the
 * DraftBlock as before.
 *
 * @param {Object}   props
 * @param {boolean}  props.visible
 * @param {string}   [props.subject]
 * @param {string}   [props.content]
 * @param {Function} props.onSave    Called with `{ subject, content }` on save.
 * @param {Function} props.onClose   Called on cancel / dismiss.
 */
const EditDraftModal = ({ visible, subject, content, onSave, onClose }) => {
  const [localSubject, setLocalSubject] = useState('');
  const [localContent, setLocalContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setLocalSubject(subject || '');
      setLocalContent(content || '');
      setSaving(false);
    }
  }, [visible, subject, content]);

  const handleSave = async () => {
    if (saving) return;
    if (!localContent.trim()) return;
    setSaving(true);
    try {
      await onSave?.({ subject: localSubject.trim(), content: localContent });
      onClose?.();
    } catch {
      setSaving(false);
    }
  };

  return (
    <Modal animationType="fade" transparent visible={visible} onRequestClose={onClose}>
      <Overlay>
        <Card>
          <Title>Edit draft</Title>
          <Hint>Tweak the message before it goes. Saving will discard the AI versions and send your edited reply.</Hint>

          {subject !== undefined && subject !== null ? (
            <>
              <Label>Subject</Label>
              <SubjectInput
                value={localSubject}
                onChangeText={setLocalSubject}
                placeholder="Subject"
                placeholderTextColor={REDESIGN_COLORS.textSecondary}
                editable={!saving}
              />
            </>
          ) : null}

          <Label>Message</Label>
          <BodyInput
            value={localContent}
            onChangeText={setLocalContent}
            placeholder="Type your reply..."
            placeholderTextColor={REDESIGN_COLORS.textSecondary}
            multiline
            textAlignVertical="top"
            editable={!saving}
          />

          <Footer>
            <CancelBtn onPress={onClose} disabled={saving}>
              <CancelBtnText>Cancel</CancelBtnText>
            </CancelBtn>
            <SaveBtn onPress={handleSave} disabled={saving || !localContent.trim()} $disabled={saving || !localContent.trim()}>
              {saving ? (
                <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
              ) : (
                <SaveBtnText>Save & send</SaveBtnText>
              )}
            </SaveBtn>
          </Footer>
        </Card>
      </Overlay>
    </Modal>
  );
};

const Overlay = styled.View`
  flex: 1;
  background-color: rgba(0, 0, 0, 0.6);
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const Card = styled.View`
  width: 100%;
  max-width: 640px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  padding: 20px;
  gap: 10px;
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 18px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const Hint = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textSecondary};
  line-height: 18px;
`;

const Label = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 11px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-top: 6px;
`;

const SubjectInput = styled(TextInput)`
  background-color: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  padding: 10px 12px;
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const BodyInput = styled(TextInput)`
  background-color: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  padding: 12px;
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
  min-height: 200px;
`;

const Footer = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

const CancelBtn = styled(Pressable)`
  padding: 10px 16px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const CancelBtnText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const SaveBtn = styled(Pressable)`
  padding: 10px 18px;
  border-radius: 8px;
  background-color: ${({ $disabled }) =>
    $disabled ? 'rgba(5, 86, 247, 0.30)' : REDESIGN_COLORS.ctaBlue};
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const SaveBtnText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

export default EditDraftModal;
